import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
} from 'recharts';
import {
	VictoryBar,
	VictoryChart,
	VictoryAxis,
	VictoryTheme,
	VictoryTooltip,
	VictoryArea,
	VictoryPolarAxis,
	VictoryScatter,
} from 'victory';
import { AuthContext } from '../AuthContext';
import ManagerNav from './managernav';
import trackerConfig from './tracker.json';


const ANALYTICS_ENDPOINT =
	process.env.REACT_APP_ANALYTICS_URL || 'https://analytics.insightos.co.uk/data/bucket';

const BUCKET_LABELS = Object.entries(trackerConfig?.buckets || {}).reduce((map, [key, value]) => {
	map[key] = value?.label || key;
	return map;
}, {});

const BUCKET_COLOR_MAP = {
	hr: '217, 70, 89', // rose
	communication: '14, 165, 233', // sky
	'task-management': '99, 102, 241', // indigo
};
const DEFAULT_BUCKET_COLOR = '79, 70, 229';
const BUCKET_CIRCLE_POSITIONS = [
	{ bucketKey: 'hr', angle: -90 },
	{ bucketKey: 'communication', angle: 30 },
	{ bucketKey: 'task-management', angle: 210 },
];

const HIGHLIGHT_COMPONENT_GROUPS = [
	{
		id: 'shifts',
		label: 'Shifts',
		description: 'publish-shifts, shift-copy',
		components: ['publish-shifts', 'shift-copy'],
	},
	{
		id: 'messages-sent',
		label: 'Messages Sent',
		description: 'message-sent',
		components: ['message-sent'],
	},
	{
		id: 'holidays-requested',
		label: 'Holidays Requested',
		description: 'holiday-request',
		components: ['holiday-request'],
	},
	{
		id: 'interviews',
		label: 'Interviews',
		description: 'interview-meeting-form',
		components: ['interview-meeting-form'],
	},
	{
		id: 'overtime-requested',
		label: 'Overtime Requested',
		description: 'overtime-manager-form',
		components: ['overtime-manager-form'],
	},
	{
		id: 'tasks-saved-today',
		label: 'Tasks Saved Today',
		description: 'save-tasks',
		components: ['save-tasks'],
	},
	{
		id: 'help-submitted',
		label: 'Help Submitted',
		description: 'bottleneck-help-actions',
		components: ['bottleneck-help-actions'],
	},
	{
		id: 'meetings-booked',
		label: 'Meetings Booked',
		description: 'meeting-form',
		components: ['meeting-form'],
	},
	{
		id: 'issues-today',
		label: 'Issues Today',
		description: 'submit-issue',
		components: ['submit-issue'],
	},
];

const getDefaultDates = () => {
	const end = new Date();
	const start = new Date();
	start.setDate(end.getDate() - 7);

	const toISODate = (date) => date.toISOString().split('T')[0];

	return {
		startDate: toISODate(start),
		endDate: toISODate(end),
	};
};

const aggregateComponentClicks = (docs = []) => {
	const componentTotals = {};

	docs.forEach((doc) => {
		const clicks = doc?.clicks || {};

		Object.entries(clicks).forEach(([component, featureMap]) => {
			const componentValue = Object.values(featureMap || {}).reduce(
				(sum, count) => sum + Number(count || 0),
				0,
			);

			componentTotals[component] = (componentTotals[component] || 0) + componentValue;
		});
	});

	return Object.entries(componentTotals).map(([component, clicks]) => ({ component, clicks }));
};

export default function AdminInteractions() {
	const user = useContext(AuthContext);
	const [filters, setFilters] = useState(getDefaultDates);
	const [rawDocs, setRawDocs] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [lastUpdated, setLastUpdated] = useState(null);
	const [salaryData, setSalaryData] = useState({ averageSalary: 0, staffLength: 0 });
	const [isSalaryLoading, setIsSalaryLoading] = useState(false);

	const companyCode = user?.companycode ?? user?.companyCode;

	const fetchAverageSalary = useCallback(async () => {
		if (!companyCode) return;

		setIsSalaryLoading(true);

		try {
			const response = await fetch('https://api.insightos.co.uk/api/average-salary', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({ companyCode }),
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch average salary: ${response.status}`);
			}

			const responseData = await response.json();

			if (responseData.success) {
				console.log('Average salary data:', responseData);
				setSalaryData({
					averageSalary: responseData.averageSalary || 0,
					staffLength: responseData.staffLength || 0,
				});
			}
		} catch (error) {
			console.error('Error fetching average salary:', error);
		} finally {
			setIsSalaryLoading(false);
		}
	}, [companyCode]);

	const fetchAnalytics = useCallback(async () => {
		if (!companyCode) {
			setError('Missing company code. Please verify your account.');
			return;
		}

		setIsLoading(true);
		setError(null);

		const performRequest = async (token) => {
			const requestUrl = new URL(ANALYTICS_ENDPOINT);
			requestUrl.searchParams.set('companycode', companyCode);
			requestUrl.searchParams.set('startDate', filters.startDate);
			requestUrl.searchParams.set('endDate', filters.endDate);

			return fetch(requestUrl.toString(), {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				credentials: 'omit',
			});
		};

		try {
			let token = user?.analyticsToken;
			if (!token && typeof user?.refreshAnalyticsToken === 'function') {
				token = await user.refreshAnalyticsToken('admin-analytics-bootstrap');
			}

			if (!token) {
				throw new Error('Missing analytics token. Please re-authenticate.');
			}

			let response = await performRequest(token);
			if (response.status === 401 && typeof user?.refreshAnalyticsToken === 'function') {
				const refreshedToken = await user.refreshAnalyticsToken('admin-analytics-retry');
				if (refreshedToken) {
					response = await performRequest(refreshedToken);
				}
			}

			if (!response.ok) {
				const message = `Analytics fetch failed (${response.status})`;
				throw new Error(message);
			}

			const payload = await response.json();
			setRawDocs(payload?.results || []);
			setLastUpdated(new Date());
		} catch (err) {
			console.error('[AdminInteractions] fetch error', err);
			setError(err.message || 'Unable to load analytics right now');
		} finally {
			setIsLoading(false);
		}
	}, [companyCode, filters.endDate, filters.startDate, user?.analyticsToken, user?.refreshAnalyticsToken]);

	useEffect(() => {
		if (companyCode && user?.analyticsToken) {
			fetchAnalytics();
			fetchAverageSalary();
		}
	}, [companyCode, user?.analyticsToken, fetchAnalytics, fetchAverageSalary]);

	const componentData = useMemo(() => {
		const aggregated = aggregateComponentClicks(rawDocs);
		return aggregated.sort((a, b) => b.clicks - a.clicks);
	}, [rawDocs]);

	const leaderboardData = useMemo(() => componentData.slice(0, 5), [componentData]);
	const totalClicks = useMemo(
		() => componentData.reduce((sum, datum) => sum + datum.clicks, 0),
		[componentData],
	);

	const highlightComponentData = useMemo(() => {
		const clickMap = componentData.reduce((acc, datum) => {
			acc[datum.component] = datum.clicks;
			return acc;
		}, {});
		return HIGHLIGHT_COMPONENT_GROUPS.map((group) => {
			const clicks = group.components.reduce((sum, key) => sum + (clickMap[key] || 0), 0);
			return { ...group, clicks };
		});
	}, [componentData]);

	const highlightMaxClicks = useMemo(
		() => highlightComponentData.reduce((max, group) => Math.max(max, group.clicks), 0),
		[highlightComponentData],
	);

	const bucketData = useMemo(() => {
		const totals = rawDocs.reduce((acc, doc) => {
			Object.entries(doc?.bucketTotals || {}).forEach(([bucketKey, count]) => {
				const clicks = Number(count || 0);
				acc[bucketKey] = (acc[bucketKey] || 0) + clicks;
			});
			return acc;
		}, {});

		return Object.entries(totals)
			.map(([bucketKey, clicks]) => ({
				bucketKey,
				label: BUCKET_LABELS[bucketKey] || bucketKey,
				clicks,
			}))
			.sort((a, b) => b.clicks - a.clicks);
	}, [rawDocs]);

	const bucketTotalClicks = useMemo(
		() => bucketData.reduce((sum, bucket) => sum + bucket.clicks, 0),
		[bucketData],
	);

	const bucketHeatmapData = useMemo(() => {
		if (bucketData.length === 0) return [];
		const maxClicks = Math.max(...bucketData.map((bucket) => bucket.clicks), 1);
		return bucketData.map((bucket) => {
			const ratio = bucket.clicks / (maxClicks || 1);
			const baseColor = BUCKET_COLOR_MAP[bucket.bucketKey] || DEFAULT_BUCKET_COLOR;
			const alpha = 0.25 + ratio * 0.65;
			return {
				...bucket,
				ratio,
				color: `rgba(${baseColor}, ${alpha.toFixed(2)})`,
			};
		});
	}, [bucketData]);

	const bucketHeatmapMap = useMemo(() => (
		bucketHeatmapData.reduce((acc, bucket) => {
			acc[bucket.bucketKey] = bucket;
			return acc;
		}, {})
	), [bucketHeatmapData]);

	const bucketRadarPoints = useMemo(() => (
		BUCKET_CIRCLE_POSITIONS.map((config) => {
			const bucket = bucketHeatmapMap[config.bucketKey] || {
				bucketKey: config.bucketKey,
				label: BUCKET_LABELS[config.bucketKey] || config.bucketKey,
				clicks: 0,
			};
			const clicks = Number(bucket.clicks || 0);
			const percentage = bucketTotalClicks ? (clicks / bucketTotalClicks) * 100 : 0;
			return {
				bucketKey: config.bucketKey,
				label: bucket.label,
				value: Number(percentage.toFixed(2)),
				clicks,
				baseColor: BUCKET_COLOR_MAP[config.bucketKey] || DEFAULT_BUCKET_COLOR,
			};
		})
	), [bucketHeatmapMap, bucketTotalClicks]);

	const bucketRadarPolygon = useMemo(() => (
		bucketRadarPoints.length > 0 ? [...bucketRadarPoints, bucketRadarPoints[0]] : []
	), [bucketRadarPoints]);

	const radarCategories = useMemo(
		() => bucketRadarPoints.map((point) => point.label || point.bucketKey),
		[bucketRadarPoints],
	);

	const totalTimeSaved = useMemo(() => {
		const total = rawDocs.reduce((sum, doc) => {
			const timeSaved = Number(doc?.totalTimeSaved || 0);
			return sum + timeSaved;
		}, 0);
		return total;
	}, [rawDocs]);

	const moneySaved = useMemo(() => {
		const hourlyRate = Number(salaryData.averageSalary || 0);
		const minutesSaved = Number(totalTimeSaved || 0);
		const hoursSaved = minutesSaved / 60;
		return hourlyRate * hoursSaved;
	}, [salaryData.averageSalary, totalTimeSaved]);

	const subscriptionCost = useMemo(() => {
		const staffCount = Number(salaryData.staffLength || 0);
		return staffCount * 15;
	}, [salaryData.staffLength]);

	const handleDateChange = (event) => {
		const { name, value } = event.target;
		setFilters((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		fetchAnalytics();
		fetchAverageSalary();
	};

	const renderEmptyState = () => (
		<div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
			<p className="text-lg font-medium">No analytics data for the selected range yet.</p>
			<p className="text-sm mt-2">Interact with tracked components to populate this dashboard.</p>
		</div>
	);

	return (
		<>
		<ManagerNav />
		<>
		{user.access === "admin" && (
		<div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-10">
			<div className="mx-auto max-w-6xl space-y-8">
				<div className="rounded-2xl bg-white p-6 shadow-sm">
					<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
						<div>
							<h1 className="text-2xl font-semibold text-slate-900">Component Interaction Analytics</h1>
							<p className="text-sm text-slate-500">
								Authenticated insights for {companyCode || 'your company'} between {filters.startDate} and {filters.endDate}.
							</p>
						</div>
						{lastUpdated && (
							<p className="text-sm text-slate-400">Last refreshed {lastUpdated.toLocaleString()}</p>
						)}
					</div>

					<form
						onSubmit={handleSubmit}
						className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-4"
					>
						<label className="flex flex-col text-sm font-medium text-slate-600">
							Start Date
							<input
								type="date"
								name="startDate"
								value={filters.startDate}
								onChange={handleDateChange}
								className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
								max={filters.endDate}
								required
							/>
						</label>

						<label className="flex flex-col text-sm font-medium text-slate-600">
							End Date
							<input
								type="date"
								name="endDate"
								value={filters.endDate}
								onChange={handleDateChange}
								className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
								min={filters.startDate}
								required
							/>
						</label>

						<div className="flex flex-col text-sm font-medium text-slate-600">
							Company Code
							<input
								type="text"
								value={companyCode || ''}
								readOnly
								className="mt-1 cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500"
							/>
						</div>

						<div className="flex items-end">
							<button
								type="submit"
								className="w-full rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
								disabled={isLoading || !companyCode}
							>
								{isLoading ? 'Loading…' : 'Refresh'}
							</button>
						</div>
					</form>

					{!user?.analyticsToken && companyCode && !error && (
						<div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
							⏳ Initializing analytics connection... This may take a few seconds.
						</div>
					)}

					{error && (
						<div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
							{error}
							{!user?.analyticsToken && (
								<div className="mt-2 text-xs">
									Tip: The analytics token is still loading. Please wait a moment and try refreshing.
								</div>
							)}
						</div>
					)}
				</div>

				{/* ROI Dashboard */}
				<div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-cyan-50 p-6 shadow-lg border border-emerald-100">
					<div className="flex items-center justify-between mb-6">
						<div>
							<h2 className="text-2xl font-bold text-slate-900">💰 Return on Investment</h2>
							<p className="text-sm text-slate-600 mt-1">Time saved vs subscription cost for the selected period</p>
						</div>
						{isSalaryLoading && (
							<span className="text-xs text-slate-500">Calculating...</span>
						)}
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						{/* Time Saved Card */}
						<div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
							<div className="flex items-center gap-3 mb-2">
								<div className="rounded-full bg-blue-100 p-2">
									<svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
								</div>
								<h3 className="text-sm font-semibold text-slate-700">Time Saved</h3>
							</div>
							<p className="text-3xl font-bold text-slate-900">{totalTimeSaved.toLocaleString()}</p>
							<p className="text-xs text-slate-500 mt-1">minutes ({(totalTimeSaved / 60).toFixed(1)} hours)</p>
						</div>

						{/* Money Saved Card */}
						<div className="rounded-xl bg-white p-6 shadow-sm border border-emerald-200">
							<div className="flex items-center gap-3 mb-2">
								<div className="rounded-full bg-emerald-100 p-2">
									<svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
								</div>
								<h3 className="text-sm font-semibold text-slate-700">Money Saved</h3>
							</div>
							<p className="text-3xl font-bold text-emerald-600">£{moneySaved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
							<p className="text-xs text-slate-500 mt-1">Based on £{salaryData.averageSalary.toFixed(2)}/hr avg salary</p>
						</div>

					</div>

					{/* Money Saved Summary */}
					<div className="mt-6 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 p-6 text-white">
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
							<div>
								<h3 className="text-lg font-bold">Money Saved</h3>
								<p className="text-sm opacity-90">Total savings from time saved</p>
							</div>
							<div className="text-right">
								<p className="text-4xl font-bold">
									£{moneySaved.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
								</p>
								<p className="text-sm opacity-90 mt-1">
									Based on £{salaryData.averageSalary.toFixed(2)}/hr avg salary
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="grid gap-6 lg:grid-cols-3">
					<div className="rounded-2xl bg-white p-6 shadow-sm lg:col-span-2">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-lg font-semibold text-slate-900">Component Click Volume</h2>
								<p className="text-sm text-slate-500">Recharts horizontal bar graph aggregated across all features.</p>
							</div>
							<span className="text-sm font-medium text-indigo-600">{totalClicks.toLocaleString()} clicks</span>
						</div>
						<div className="mt-4 h-96">
							{componentData.length === 0 ? (
								renderEmptyState()
							) : (
								<ResponsiveContainer width="100%" height="100%">
									<BarChart
										data={componentData}
										layout="vertical"
										margin={{ top: 10, right: 20, left: 50, bottom: 10 }}
									>
										<CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
										<XAxis type="number" stroke="#475569" />
										<YAxis dataKey="component" type="category" width={120} stroke="#475569" />
										<Tooltip cursor={{ fill: '#f1f5f9' }} formatter={(value) => [value, 'Clicks']} />
										<Bar dataKey="clicks" fill="#6366f1" radius={[0, 6, 6, 0]} />
									</BarChart>
								</ResponsiveContainer>
							)}
						</div>
					</div>

					<div className="rounded-2xl bg-white p-6 shadow-sm">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-lg font-semibold text-slate-900">Component Leaderboard</h2>
								<p className="text-sm text-slate-500">Top performers visualized with Victory charts.</p>
							</div>
							<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
								Top {leaderboardData.length || 0}
							</span>
						</div>

						<div className="mt-4 h-64">
							{leaderboardData.length === 0 ? (
								renderEmptyState()
							) : (
								<VictoryChart
									theme={VictoryTheme.material}
									domainPadding={20}
									padding={{ top: 20, bottom: 60, left: 60, right: 20 }}
								>
									<VictoryAxis tickFormat={(tick) => tick}
										style={{ tickLabels: { angle: -30, fontSize: 10, fill: '#475569' } }}
									/>
									<VictoryAxis dependentAxis tickFormat={(tick) => `${tick}`} style={{ tickLabels: { fontSize: 10 } }} />
									<VictoryBar
										data={leaderboardData.map((item) => ({ x: item.component, y: item.clicks }))}
										cornerRadius={{ top: 4, bottom: 4 }}
										style={{ data: { fill: '#22d3ee', width: 20 } }}
										labels={({ datum }) => `${datum.x}: ${datum.y}`}
										labelComponent={<VictoryTooltip constrainToVisibleArea />}
									/>
								</VictoryChart>
							)}
						</div>

						<ul className="mt-4 space-y-3">
							{leaderboardData.map((item, index) => (
								<li
									key={item.component}
									className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3"
								>
									<div>
										<p className="text-sm font-semibold text-slate-900">{index + 1}. {item.component}</p>
										<p className="text-xs text-slate-500">{((item.clicks / totalClicks) * 100 || 0).toFixed(1)}% of clicks</p>
									</div>
									<span className="text-base font-bold text-slate-800">{item.clicks.toLocaleString()}</span>
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="rounded-2xl bg-white p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-lg font-semibold text-slate-900">Bucket Distribution</h2>
							<p className="text-sm text-slate-500">HR, Communication, and Task Management engagement.</p>
						</div>
						<span className="text-sm font-medium text-indigo-600">{bucketTotalClicks.toLocaleString()} clicks</span>
					</div>

					{bucketData.length === 0 ? (
						<div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
							No bucket-level activity for this range yet.
						</div>
					) : (
						<>
							<div className="mt-6">
								<p className="text-sm font-semibold text-slate-600">Heatmap</p>
								<div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-center">
									<div className="mx-auto w-full max-w-md flex-1">
										<div className="rounded-3xl border border-slate-100 bg-gradient-to-b from-white via-slate-50 to-white p-4 shadow-inner">
											<VictoryChart
												polar
												theme={VictoryTheme.material}
												domain={{ y: [0, 100] }}
												padding={{ top: 50, bottom: 50, left: 70, right: 70 }}
												categories={{ x: radarCategories }}
											>
												<VictoryPolarAxis
													style={{ tickLabels: { fill: '#0e64ddff', fontSize: 9, fontWeight: 500 } }}
													tickValues={radarCategories}
												/>
												
												<VictoryArea
													data={bucketRadarPolygon.map((point) => ({ x: point.label, y: point.value }))}
													style={{
														data: {
															fill: 'rgba(79, 70, 229, 0.25)',
															stroke: '#4338ca',
															strokeWidth: 2,
														},
													}}
												/>
												<VictoryScatter
													data={bucketRadarPoints.map((point) => ({ x: point.label, y: point.value, clicks: point.clicks ?? 0 }))}
													size={6}
													style={{ data: { fill: '#1d4ed8' }, labels: { fill: '#0f172a', fontSize: 10, fontWeight: 500 } }}
													labels={({ datum }) => `${Math.round(datum.y)}% · ${datum.clicks ?? 0} clicks`}
													labelComponent={<VictoryTooltip constrainToVisibleArea cornerRadius={8} flyoutStyle={{ fill: 'white', stroke: '#e2e8f0' }} />}
											/>
											</VictoryChart>

											<div className="text-center text-sm text-slate-500">
												Total bucket clicks: <span className="font-semibold text-slate-900">{bucketTotalClicks.toLocaleString()}</span>
											</div>
										</div>
									</div>

									<div className="flex-1 space-y-3">
										{bucketRadarPoints.map((point) => (
											<div key={`${point.bucketKey}-legend`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
												<div className="flex items-center justify-between text-sm font-semibold text-slate-700">
													<span className="flex items-center gap-2">
														<span
															className="inline-flex h-2.5 w-2.5 rounded-full"
															style={{ backgroundColor: `rgb(${point.baseColor})` }}
														/>
														{point.label}
													</span>
													<span className="text-slate-500">{Math.round(point.value)}%</span>
												</div>
												<div className="mt-2 h-2 w-full rounded-full bg-white">
													<div
														className="h-full rounded-full"
														style={{ width: `${point.value}%`, backgroundColor: `rgba(${point.baseColor}, 0.8)` }}
													/>
												</div>
												<p className="mt-1 text-xs text-slate-500">
													{(point.clicks ?? 0).toLocaleString()} clicks
												</p>
											</div>
										))}
									</div>
								</div>
							</div>

							{/* Detailed list removed to avoid duplicate percentage display */}
						</>
					)}
				</div>
			</div>

			<div className="rounded-2xl bg-white p-6 shadow-sm">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-lg font-semibold text-slate-900">Key Activity Table</h2>
						<p className="text-sm text-slate-500">Tracks specific data-components with visual click totals.</p>
					</div>
					<span className="text-sm font-medium text-slate-500">{highlightComponentData.reduce((sum, group) => sum + group.clicks, 0).toLocaleString()} tracked clicks</span>
				</div>

				<div className="mt-6 overflow-x-auto">
					<table className="min-w-full divide-y divide-slate-100 text-sm">
						<thead>
							<tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
								<th className="py-2">Activity</th>
								<th className="py-2">Data Components</th>
								<th className="py-2 text-right">Clicks</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{highlightComponentData.map((group) => {
								const widthPercent = highlightMaxClicks ? (group.clicks / highlightMaxClicks) * 100 : 0;
								return (
									<tr key={group.id}>
										<td className="py-3 pr-4">
											<p className="text-sm font-semibold text-slate-900">{group.label}</p>
											<p className="text-xs text-slate-500">{group.description}</p>
										</td>
										<td className="py-3 pr-4 text-xs text-slate-500">{group.components.join(', ')}</td>
										<td className="py-3">
											<div className="text-right text-sm font-semibold text-slate-900">{group.clicks.toLocaleString()}</div>
											<div className="mt-2 h-2 rounded-full bg-slate-100">
												<div
													className="h-full rounded-full bg-indigo-500"
													style={{ width: `${widthPercent}%` }}
												/>
											</div>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
		)}
		</>
		
		{user.access !== "admin" && (
		<div className="min-h-screen flex items-center justify-center bg-slate-100 px-4 py-8 sm:px-6 lg:px-10">
			<div className="rounded-2xl bg-white p-6 shadow-sm">
				<h1 className="text-2xl font-semibold text-slate-900">Access Denied</h1>
				<p className="mt-4 text-slate-500">You do not have the necessary permissions to view this page. Please contact your administrator if you believe this is an error.</p>
			</div>
		</div>
		)}
		

		</>
	);
}
